import React, { useState, useEffect } from 'react';
import { X, TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react';
import { adminAnalytics } from '../../services/api';
import TrendChart from './TrendChart';

/**
 * MetricDrawer — Slide-out detail panel for a selected metric.
 *
 * Props:
 *   metricKey: string (from controlled registry)
 *   filters: { days, compare }
 *   onClose: () => void
 */

const MetricDrawer = ({ metricKey, filters = {}, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!metricKey) return;
        setLoading(true);
        adminAnalytics.getMetricDetail({
            metric: metricKey,
            days: filters.days || 7,
            compare: filters.compare || false,
        }).then(res => {
            setData(res.data);
        }).catch(err => {
            console.error('Metric detail failed:', err);
            setData(null);
        }).finally(() => setLoading(false));
    }, [metricKey, filters.days, filters.compare]);

    if (!metricKey) return null;

    const deltaColor = data?.delta_pct > 0 ? '#10b981'
        : data?.delta_pct < 0 ? '#ef4444'
        : '#6b7280';

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)',
                    zIndex: 100, transition: 'opacity 0.2s',
                }}
            />

            {/* Drawer */}
            <div style={{
                position: 'fixed', top: 0, right: 0, bottom: 0,
                width: 420, maxWidth: '90vw',
                background: '#fff', zIndex: 101,
                boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
                display: 'flex', flexDirection: 'column',
                animation: 'slideInRight 0.2s ease-out',
            }}>
                {/* Header */}
                <div style={{
                    padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <div>
                        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>
                            {data?.title || metricKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </h3>
                        <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0' }}>
                            {data?.description || 'Metric detail'}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            padding: 6, borderRadius: 8, border: 'none',
                            background: '#f1f5f9', cursor: 'pointer',
                            color: '#64748b', transition: 'all 0.15s',
                        }}
                        onMouseOver={e => e.currentTarget.style.background = '#e2e8f0'}
                        onMouseOut={e => e.currentTarget.style.background = '#f1f5f9'}
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
                    {loading ? (
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            minHeight: 200, flexDirection: 'column', gap: 10,
                        }}>
                            <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
                            <span style={{ fontSize: 13, color: '#6b7280' }}>Loading detail…</span>
                        </div>
                    ) : !data ? (
                        <div style={{
                            textAlign: 'center', padding: '40px 0',
                            color: '#94a3b8', fontSize: 13,
                        }}>
                            No detail data available for this metric.
                        </div>
                    ) : (
                        <>
                            {/* Current value */}
                            <div style={{
                                background: '#f8fafc', borderRadius: 12, padding: 20,
                                marginBottom: 20, textAlign: 'center',
                            }}>
                                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>
                                    Current Value
                                </div>
                                <div style={{
                                    fontSize: 36, fontWeight: 800, color: '#111827',
                                    fontVariantNumeric: 'tabular-nums',
                                }}>
                                    {typeof data.current_value === 'number'
                                        ? (metricKey.includes('rate') || metricKey.includes('pct')
                                            ? `${data.current_value}%`
                                            : data.current_value.toLocaleString())
                                        : data.current_value ?? '—'}
                                </div>

                                {/* Compare */}
                                {data.compare && data.previous_value !== null && (
                                    <div style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        gap: 8, marginTop: 10,
                                    }}>
                                        <span style={{ fontSize: 12, color: '#6b7280' }}>
                                            Previous: {typeof data.previous_value === 'number'
                                                ? data.previous_value.toLocaleString()
                                                : data.previous_value}
                                        </span>
                                        {data.delta_pct !== null && (
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                                fontSize: 12, fontWeight: 600, color: deltaColor,
                                                background: data.delta_pct > 0 ? '#ecfdf5' : data.delta_pct < 0 ? '#fef2f2' : '#f3f4f6',
                                                padding: '2px 8px', borderRadius: 6,
                                            }}>
                                                {data.delta_pct > 0 ? <TrendingUp size={12} /> : data.delta_pct < 0 ? <TrendingDown size={12} /> : <Minus size={12} />}
                                                {data.delta_pct > 0 ? '+' : ''}{data.delta_pct}%
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Trend chart */}
                            {data.trend && data.trend.length > 0 && (
                                <div style={{ marginBottom: 20 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                                        Daily Trend ({data.period_days}d)
                                    </div>
                                    <div style={{
                                        background: '#f8fafc', borderRadius: 10,
                                        padding: '12px 8px', border: '1px solid #f1f5f9',
                                    }}>
                                        <TrendChart
                                            data={data.trend}
                                            height={100}
                                            color="#6366f1"
                                            type="bar"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Period info */}
                            <div style={{
                                fontSize: 11, color: '#94a3b8', textAlign: 'center',
                                padding: '12px 0', borderTop: '1px solid #f1f5f9',
                            }}>
                                {data.period_days}-day period · {data.compare ? 'Comparison enabled' : 'No comparison'}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes slideInRight {
                    from { transform: translateX(100%); }
                    to { transform: translateX(0); }
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </>
    );
};

export default MetricDrawer;
